import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input } from '@angular/core';
import { moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

import { ERadioGroupType } from '@ui/sp-radio-group';
import { HtmlToTextPipe } from '@common/pipes/html-to-text.pipe';

import type { AccordionEntity, NarrowWidgetPositionLegacy } from '@sites/entity-core';
import type { AccordionForm, AccordionItem } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { PaidAccessNotificationContainerComponent } from '@web/shared/components/web-settings/paid-access-notification/paid-access-notification-container.component';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';

import type { OnInit } from '@angular/core';

/**
 * Standalone, `input()`-based control panel for the `accordion` widget. No sub-panel editor exists
 * for a single item — titles/content are edited in place in the view via `web-editor`, the control
 * only offers drag-reorder, delete, and "jump to this item" (`setActiveItem`).
 *
 * **`setActiveItem()` is untouched from the legacy component, verbatim** — same reasoning as the
 * view's `toggleItem()`: pure imperative DOM, never reads or writes the form. Only its
 * `accordionList.length` read moved from the old `UntypedFormArray` to the form's `accordionItems()`
 * computed, and `getWidgetId` now sources from `form.displayId()`.
 */
@Component({
    selector: 'web-accordion-control',
    templateUrl: './accordion-control.component.html',
    styleUrls: ['./accordion-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        DragDropModule,
        WebColorPickerComponent,
        HtmlToTextPipe,
        PaidAccessNotificationContainerComponent,
        WebWidgetPositionSettingsComponent,
        WebWidgetHideSettingsComponent,
    ],
})
export class AccordionControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<AccordionEntity['data']>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    public readonly ERadioGroupType = ERadioGroupType;
    public lastOpenItem: number = 0;

    public readonly isDeviceDesktopSig = toSignal(this.layoutFacade.isDeviceDesktop$, { initialValue: true });
    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });
    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });

    public form!: AccordionForm;

    public readonly accordionList = computed<AccordionItem[]>(() => this.form.accordionItems());

    public readonly position = computed<NarrowWidgetPositionLegacy | null>(() => this.form.position().value() ?? null);

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<AccordionForm>(this.widget().id, this.destroyRef);
    }

    public get getWidgetId(): string {
        return this.form.displayId();
    }

    public onPositionChange(patch: Partial<NarrowWidgetPositionLegacy>): void {
        this.form.setPosition({ ...(this.position() ?? {}), ...patch });
    }

    public setSynchronizeStyle(): void {
        this.form.setIsSynchronizeStyle(!this.form.isSynchronized());
    }

    /**
     * `isOpen`/`contentHeight` are seeded explicitly here — the legacy control's `addAccordion()`
     * omitted `isOpen` entirely, tolerated only because the old `dataAdapter` (which signal-form
     * widgets don't have) force-overwrote it on next load. The entity schema now requires it.
     */
    public addAccordion(): void {
        this.form.addAccordionItem({
            title: `<h2>${this.translateService.instant('text_element_header')}</h2>`,
            content: `<p>${this.translateService.instant('text_element_text')}</p>`,
            isOpen: false,
            contentHeight: '0px',
        });
    }

    public setIsAutoCollapse(value: boolean): void {
        this.form.setIsAutoCollapse(value);
    }

    public setIconType(value: string): void {
        this.form.setIconType(value as AccordionEntity['data']['iconType']);
    }

    public setIsIndent(value: boolean): void {
        this.form.setIsIndent(value);
    }

    public setIconColor({ value, presetName }: ColorPickerEventPayload): void {
        this.form.setIconColor(value, presetName);
    }

    public setIndentColor({ value, presetName }: ColorPickerEventPayload): void {
        this.form.setIndentColor(value, presetName);
    }

    public setHorizontalAlign(value: string): void {
        this.form.setHorizontalAlign(value as AccordionEntity['data']['horizontalAlign']);
    }

    public setHideOnMobile(value: boolean): void {
        this.form.setHideOnMobile(value);
    }

    public setHideOnTablet(value: boolean): void {
        this.form.setHideOnTablet(value);
    }

    public setHideOnDesktop(value: boolean): void {
        this.form.setHideOnDesktop(value);
    }

    public setActiveItem(index: number) {
        const accordionList = [...this.accordionList()];

        const currentParentItem = document.querySelector(`[id='${this.getWidgetId}'] .itemContent_${index} .sp-editor`)
            .parentNode.parentNode as HTMLDivElement;

        const visibleItemHeight =
            (
                document.querySelector(`[id='${this.getWidgetId}'] .itemContent_${index} .sp-editor`) as HTMLDivElement
            )?.offsetHeight.toString() + 'px';

        if (this.form.isAutoCollapse().value()) {
            const visibleItem = document.querySelector(
                `[id='${this.getWidgetId}'] .itemContent_${index}`
            ) as HTMLDivElement;
            const openControl = document.querySelector(
                `[id='${this.getWidgetId}'] .itemControl_${index}`
            ) as HTMLDivElement;

            if (visibleItem.classList.contains('active')) {
                for (let i = 0; i < accordionList.length; i++) {
                    const hiddenItem = document.querySelector(
                        `[id='${this.getWidgetId}'] .itemContent_${i}`
                    ) as HTMLDivElement;
                    const closeControl = document.querySelector(
                        `[id='${this.getWidgetId}'] .itemControl_${i}`
                    ) as HTMLDivElement;
                    closeControl.classList.remove('active');
                    hiddenItem.classList.remove('active');
                    hiddenItem.style.height = '0px';
                }

                return (this.lastOpenItem = -1);
            } else {
                for (let i = 0; i < accordionList.length; i++) {
                    const hiddenItem = document.querySelector(
                        `[id='${this.getWidgetId}'] .itemContent_${i}`
                    ) as HTMLDivElement;
                    const closeControl = document.querySelector(
                        `[id='${this.getWidgetId}'] .itemControl_${i}`
                    ) as HTMLDivElement;
                    closeControl.classList.remove('active');
                    hiddenItem.classList.remove('active');
                    hiddenItem.style.height = '0px';
                }
                openControl.classList.add('active');
                visibleItem.classList.add('active');
                visibleItem.style.height = visibleItemHeight;

                setTimeout(() => {
                    visibleItem.style.height = 'auto';
                }, 350);

                currentParentItem.classList.add('outlined');
                setTimeout(() => currentParentItem.classList.remove('outlined'), 1500);

                return (this.lastOpenItem = index);
            }
        } else {
            const currentItem = document.querySelector(
                `[id='${this.getWidgetId}'] .itemContent_${index}`
            ) as HTMLDivElement;
            const currentControl = document.querySelector(
                `[id='${this.getWidgetId}'] .itemControl_${index}`
            ) as HTMLDivElement;

            if (currentItem.classList.contains('active')) {
                currentControl.classList.remove('active');
                currentItem.classList.remove('active');
                currentItem.style.height = '0px';
            } else {
                currentControl.classList.add('active');
                currentItem.classList.add('active');
                currentItem.style.height = visibleItemHeight;

                setTimeout(() => {
                    currentItem.style.height = 'auto';
                }, 350);

                currentParentItem.classList.add('outlined');
                setTimeout(() => currentParentItem.classList.remove('outlined'), 1500);
            }
        }
    }

    public removeAccordion(index: number): void {
        if (this.accordionList().length === 1) {
            return;
        }

        this.form.removeAccordionItem(index);
    }

    public dropElementItem(event: CdkDragDrop<AccordionItem[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        this.form.moveAccordionItem(event.previousIndex, event.currentIndex);
    }
}
